import React, { useMemo, useState } from 'react';
import {
  Button, HStack, ModalBody, ModalFooter, ModalHeader,
  Text, VStack, Divider, Checkbox,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import {
  MdCancel, MdContentCopy, MdCheckBox, MdCheckBoxOutlineBlank,
} from 'react-icons/md';

import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { getSourceFields, getDestinationFields, COPY_OPTIONS_FIELDS } from './copyConfigFormConfig';
import { useCopyConfigForm } from '../../hooks/useCopyConfigForm';
import {
  validateFormData, submitCopyConfig, getDestinationDisplay,
} from './copyConfigUtils';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';

const COPY_OPTION_KEYS = COPY_OPTIONS_FIELDS.map((field) => field.name);

const OptionSection = ({
  formData, handleChange, allOptionsSelected, handleSelectAll, handleDeselectAll,
}) => (
  <VStack align="stretch" spacing={3}>
    <HStack justify="flex-end" align="center">
      {!allOptionsSelected ? (
        <Button
          size="xs" variant="ghost"
          colorScheme="brand" onClick={handleSelectAll}
          leftIcon={<MdCheckBox />}
          aria-label="Select all"
        >
          Select All
        </Button>
      ) : (
        <Button
          size="xs" variant="ghost"
          colorScheme="gray" onClick={handleDeselectAll}
          leftIcon={<MdCheckBoxOutlineBlank />}
          _hover={{ bg: 'brand.700' }}
          aria-label="Deselect all"
        >
          Deselect All
        </Button>
      )}
    </HStack>
    <VStack align="stretch" spacing={2}>
      {COPY_OPTIONS_FIELDS.map((field) => (
        <Checkbox
          key={field.name}
          size="md"
          colorScheme="brand"
          name={field.name}
          isChecked={!!formData[field.name]}
          onChange={handleChange}
        >
          {field.label}
        </Checkbox>
      ))}
    </VStack>
  </VStack>
);

const InfoSection = ({ label, value }) => (
  <HStack justify="space-between">
    <Text color="whiteAlpha.700" fontSize="sm">{label}:</Text>
    <Text color="white" fontSize="sm" fontWeight="medium">{value}</Text>
  </HStack>
);

const DestSectionSidebar = ({ entity, subClientName, payoutCurrency }) => (
  <VStack align="stretch" spacing={2} bg="blackAlpha.300" p={3} borderRadius="md">
    <InfoSection label="Destination Client" value={entity?.name?.value} />
    <InfoSection label="Destination SubClient" value={subClientName} />
    <InfoSection label="Destination Currency" value={payoutCurrency} />
  </VStack>
);

const FormFieldsSection = ({ fields, formData, handleChange }) => (
  <VStack align="stretch" spacing={3}>
    {fields.map((field) => (
      <DynamicFormField
        key={field.name}
        fieldConfig={field}
        data={formData}
        handleChange={handleChange}
      />
    ))}
  </VStack>
);

const CopyConfigurationModal = ({
  onClose, entity, payoutCurrency, subClientName, fromSidebar = true,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  // --- Redux State ---
  const clientHierarchy = useSelector(
    (state) => state.falconDashboard.config.data?.clientHierarchy || {},
  );

  const allClients = useMemo(() => Object.keys(clientHierarchy).sort(), [clientHierarchy]);
  const availableClients = useMemo(
    () => fromSidebar
      ? allClients.filter((clientName) => clientName !== entity?.name?.value)
      : allClients,
    [allClients, fromSidebar, entity],
  );

  // --- Custom Hook for Form Logic ---
  const {
    formData, handleChange,
    sourceSubClients, sourceCurrencies,
    destinationClients, destinationSubClients, destinationCurrencies,
  } = useCopyConfigForm({
    clientHierarchy, availableClients, allClients, fromSidebar,
  });

  // --- Field Configurations ---
  const sourceFields = useMemo(
    () => getSourceFields({ clients: availableClients, sourceSubClients, sourceCurrencies }),
    [availableClients, sourceSubClients, sourceCurrencies],
  );

  const destinationFields = useMemo(() => {
    if (fromSidebar) return [];
    return getDestinationFields({
      clients: destinationClients, sourceSubClients: destinationSubClients, sourceCurrencies: destinationCurrencies,
    });
  }, [fromSidebar, destinationClients, destinationSubClients, destinationCurrencies]);

  const allOptionsSelected = useMemo(
    () => COPY_OPTION_KEYS.every((key) => formData[key]),
    [formData],
  );

  // --- Select/Deselect All Options Handler ---
  const setAllCopyOptions = (checked) => {
    COPY_OPTION_KEYS.forEach((key) => {
      handleChange({
        target: {
          name: key,
          value: checked,
          type: 'checkbox',
          checked,
        },
      });
    });
  };

  // --- Submit Handler ---
  const handleSubmit = async () => {
    const validation = validateFormData(formData, fromSidebar);
    if (!validation.isValid) {
      dispatch(setAlert({
        title: `Invalid Selection: ${validation.error}`,
        status: ALERT_STATUS_TYPE.ERROR,
      }));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        source: {
          client: formData.sourceClient,
          sub_client: formData.sourceSubClient,
          currency: formData.sourceCurrency,
        },
        destination: {
          client: fromSidebar ? entity?.name?.value : formData.destinationClient,
          sub_client: fromSidebar ? subClientName : formData.destinationSubClient,
          currency: fromSidebar ? payoutCurrency : formData.destinationCurrency,
        },
        copy_options: {
          copy_limits: formData.copyLimits,
          copy_payout_vendors: formData.copyPayoutVendors,
          copy_exchange_settings: formData.copyExchangeSettings,
          copy_sla_settings: formData.copySlaSettings,
          copy_fee_settings: formData.copyFeeSettings,
        },
      };
      await submitCopyConfig(payload);

      const destDisplay = getDestinationDisplay(formData, fromSidebar, entity, subClientName);
      dispatch(setAlert({
        title: `Configuration Copied Successfully from ${formData.sourceClient} (${formData.sourceSubClient}) to ${destDisplay}`,
        status: ALERT_STATUS_TYPE.SUCCESS,
      }));
      onClose();
    } catch (error) {
      console.error('Copy configuration error:', error);
      dispatch(setAlert({
        title: `Copy Failed: ${error?.response?.data?.message || error?.message || 'Failed to copy configuration.'}`,
        status: ALERT_STATUS_TYPE.ERROR,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // --- No Clients Available ---
  if (availableClients.length === 0) {
    return (
      <>
        <ModalHeader color="brand.500">Copy Payout Configuration from Source to Destination</ModalHeader>
        <ModalBody>
          <Text color="whiteAlpha.700" fontSize="sm" textAlign="center">
            {fromSidebar ? 'No other clients available to copy from' : 'No clients available'}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            colorScheme="gray"
            size="sm"
            onClick={onClose}
            leftIcon={<MdCancel />}
            aria-label="Close"
          >
            Close
          </Button>
        </ModalFooter>
      </>
    );
  }

  // --- Main Modal ---
  return (
    <>
      <ModalHeader color="brand.500">Copy Payout Configuration</ModalHeader>
      <ModalBody>
        <VStack align="stretch" spacing={4}>
          {/* Source Section */}
          <FormFieldsSection fields={sourceFields} formData={formData} handleChange={handleChange}/>
          
          <Divider borderColor="whiteAlpha.300" />

          {/* Destination Section */}
          {fromSidebar ? (
            <DestSectionSidebar entity={entity} subClientName={subClientName} payoutCurrency={payoutCurrency}/>
          ) : (
            <FormFieldsSection fields={destinationFields} formData={formData} handleChange={handleChange}/>
          )}
          
          <Divider borderColor="whiteAlpha.300" />

          {/* Copy Options */}
          <OptionSection
            formData={formData}
            handleChange={handleChange}
            allOptionsSelected={allOptionsSelected}
            handleSelectAll={() => setAllCopyOptions(true)}
            handleDeselectAll={() => setAllCopyOptions(false)}
          />
        </VStack>
      </ModalBody>
      <ModalFooter>
        <HStack spacing={3}>
          <Button
            variant="outline"
            colorScheme="gray"
            size="sm"
            onClick={onClose}
            isDisabled={isLoading}
            leftIcon={<MdCancel />}
            aria-label="Cancel"
          >
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            size="sm"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading}
            leftIcon={<MdContentCopy />}
            bg="brand.600"
            _hover={{ bg: 'brand.700' }}
            aria-label="Replicate Configurations"
          >
            Replicate Configurations
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default CopyConfigurationModal;
